import { Injectable } from '@angular/core';
import { concatMap, forkJoin, mergeMap, Observable, of } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class IndexedDBService {
    private dbName = 'StorytellingDB';
    private storeName = 'navigation';
    private connection!: IDBDatabase;

    connect(): Observable<IDBDatabase> {
        return new Observable<IDBDatabase>(observer => {
            const request = indexedDB.open(this.dbName, 1);
            request.onupgradeneeded = (event: any) => {
                this.connection = event.target.result;
                if (!this.connection.objectStoreNames.contains(this.storeName)) {
                    this.connection.createObjectStore(this.storeName, { keyPath: 'DBGuid' });
                }
            };

            request.onsuccess = (event: any) => {
                console.info('Database initialized successfully');
                this.connection = event.target.result;
                observer.next(this.connection);
                observer.complete();
            };

            request.onerror = error => {
                console.error('Error initializing database: ', error);
                observer.error(error);
            };
        });
    }

    db(): Observable<IDBDatabase> {
        return this.connection ? of(this.connection) : this.connect();
    }

    add(data: any): Observable<IDBValidKey> {
        return this.db().pipe(
            concatMap(db => {
                return new Observable<IDBValidKey>(observer => {
                    try {
                        const tx = db.transaction(this.storeName, 'readwrite');
                        const store = tx.objectStore(this.storeName);
                        const request = store.add(data);

                        request.onsuccess = () => {
                            observer.next(request.result);
                            observer.complete();
                        };
                        request.onerror = () => {
                            observer.error(request.error);
                        };
                    } catch (err) {
                        observer.error(err);
                    }
                });
            })
        );
    }

    many(keys: string[]): Observable<any[]> {
        return forkJoin(keys.map(key => this.get(key)));
    }

    get(key: string): Observable<any> {
        return this.db().pipe(
            mergeMap(db => {
                return new Observable<any>(observer => {
                    try {
                        const tx = db.transaction(this.storeName, 'readwrite');
                        const store = tx.objectStore(this.storeName);
                        const request = store.get(key);

                        request.onsuccess = () => {
                            observer.next(request.result);
                            observer.complete();
                        };
                        request.onerror = () => {
                            console.warn(request.error);
                            observer.error(request.error);
                        };
                    } catch (err) {
                        console.warn(err);
                        observer.error(err);
                    }
                });
            })
        );
    }

    update(data: any): Observable<IDBValidKey> {
        return this.db().pipe(
            concatMap(db => {
                return new Observable<IDBValidKey>(observer => {
                    try {
                        const tx = db.transaction(this.storeName, 'readwrite');
                        const store = tx.objectStore(this.storeName);
                        const request = store.put(data);

                        request.onsuccess = () => {
                            observer.next(request.result);
                            observer.complete();
                        };
                        request.onerror = () => {
                            console.warn(request.error);
                            return observer.error(request.error);
                        };
                    } catch (err) {
                        console.warn(err);
                        observer.error(err);
                    }
                });
            })
        );
    }

    delete(key: number): Observable<void> {
        return this.db().pipe(
            concatMap(db => {
                return new Observable<void>(observer => {
                    try {
                        const tx = db.transaction(this.storeName, 'readwrite');
                        const store = tx.objectStore(this.storeName);
                        const request = store.delete(key);

                        request.onsuccess = () => {
                            observer.next();
                            observer.complete();
                        };
                        request.onerror = () => observer.error(request.error);
                    } catch (err) {
                        observer.error(err);
                    }
                });
            })
        );
    }
}
